/**
 * プレビュー用テロップユーティリティ。
 * 折り返し・スタイルマージは core、描画は subtitleRender に委譲し、
 * ここは DOM（Image / HTMLCanvasElement）との橋渡しに徹する。
 * 動画生成Workerと同じロジックを通るため、プレビューと出力は常に一致する。
 */
import type { SubtitleStyle, SubtitleSettings } from '@/types';
import {
  wrapJapaneseText,
  subtitleFontString,
  SUBTITLE_MAX_LINES,
  type TextMeasurer,
  type WrapResult,
} from '@/core/subtitleLayout';
import { fitRect } from '@/core/layout';
import { drawSubtitle } from './subtitleRender';

export { mergeSubtitleStyle } from '@/core/subtitleLayout';
export type { WrapResult } from '@/core/subtitleLayout';

// テキスト幅計測用の使い回しCanvas
let measureCanvas: HTMLCanvasElement | null = null;

function createMeasurer(style: SubtitleStyle): TextMeasurer {
  if (!measureCanvas) {
    measureCanvas = document.createElement('canvas');
  }
  const ctx = measureCanvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context is not available');
  }
  // Canvasは共有なので、fontは計測のたびに設定する
  //（複数の計測器を作ってから使う呼び出し順でも正しく測れるように）
  const font = subtitleFontString(style);
  return (text) => {
    ctx.font = font;
    return ctx.measureText(text).width;
  };
}

// テロップテキストを検証
export interface SubtitleValidation {
  isValid: boolean;
  lines: string[];
  exceedsMaxLines: boolean;
  actualLineCount: number;
  warningMessage?: string;
}

export function validateSubtitleText(
  text: string,
  videoWidth: number,
  subtitleSettings: SubtitleSettings,
  style: SubtitleStyle
): SubtitleValidation {
  const maxWidth = videoWidth - subtitleSettings.horizontalPadding * 2;

  const wrapResult = wrapJapaneseText(
    text,
    maxWidth,
    subtitleSettings.letterSpacing,
    createMeasurer(style)
  );

  const { exceedsMaxLines, actualLineCount } = wrapResult;

  return {
    isValid: !exceedsMaxLines,
    lines: wrapResult.lines,
    exceedsMaxLines,
    actualLineCount,
    warningMessage: exceedsMaxLines
      ? `テロップが${SUBTITLE_MAX_LINES}行を超えています（実際は${actualLineCount}行）。テキストを短くするか、フォントサイズを小さくしてください。`
      : undefined,
  };
}

// プレビュー用のテロップ画像を生成（動画生成と同じロジックを使用）
export async function generateSubtitlePreview(
  imageUrl: string,
  subtitleText: string,
  subtitleSettings: SubtitleSettings,
  style: SubtitleStyle,
  videoWidth: number = 1920,
  videoHeight: number = 1080
): Promise<{ dataUrl: string; wrapResult: WrapResult }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // 動画と同じ解像度でCanvasを作成
      const canvas = document.createElement('canvas');
      canvas.width = videoWidth;
      canvas.height = videoHeight;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Canvas 2D context is not available'));
        return;
      }

      // 背景を黒で塗りつぶし、画像をアスペクト比を維持してフィット描画
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, videoWidth, videoHeight);

      const rect = fitRect(img.width, img.height, videoWidth, videoHeight);
      ctx.drawImage(img, rect.x, rect.y, rect.width, rect.height);

      // テロップを描画（動画生成と同じロジック）
      const wrapResult = drawSubtitle(ctx, subtitleText, videoWidth, videoHeight, subtitleSettings, style);

      resolve({
        dataUrl: canvas.toDataURL('image/png'),
        wrapResult,
      });
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageUrl;
  });
}
